import React, { useRef, useState, useEffect, useCallback } from "react";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Trash, UploadSimple, FilePdf, Image as ImageIcon } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function ProjectFiles({ projectId, lang = "en" }) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [thumbs, setThumbs] = useState({}); // id -> blob URL
  const inputRef = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const r = await api.get(`/files/list`, { params: { project_id: projectId } });
      setFiles(r.data);
    } catch (e) {
      console.error("Failed to load file list:", e);
    }
  }, [projectId]);

  useEffect(() => { refresh(); }, [refresh]);

  // Load image thumbnails as blobs (auth headers)
  useEffect(() => {
    const cancelled = { current: false };
    files.forEach(async (f) => {
      if (!f.content_type?.startsWith("image/")) return;
      if (thumbs[f.id]) return;
      try {
        const r = await api.get(`/files/${f.id}/download`, { responseType: "blob" });
        if (cancelled.current) return;
        const url = URL.createObjectURL(r.data);
        setThumbs((t) => ({ ...t, [f.id]: url }));
      } catch (e) {
        console.warn("Thumbnail load failed for file", f.id, e);
      }
    });
    return () => { cancelled.current = true; };
    // eslint-disable-next-line
  }, [files]);

  const handleFiles = async (list) => {
    if (!list?.length) return;
    setUploading(true);
    for (const f of list) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        await api.post(`/files/upload?project_id=${projectId}`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } catch (e) {
        toast.error(`${f.name}: ${e?.response?.data?.detail || "Upload failed"}`);
      }
    }
    setUploading(false);
    refresh();
    toast.success(lang === "fr" ? "Téléversé" : "Uploaded");
  };

  const onDrop = (e) => {
    e.preventDefault();
    handleFiles(e.dataTransfer.files);
  };

  const remove = async (id) => {
    await api.delete(`/files/${id}`);
    refresh();
  };

  return (
    <div className="border border-zinc-200">
      <div className="border-b border-zinc-200 px-5 py-3 flex justify-between items-center">
        <p className="label-eyebrow text-zinc-500">{lang === "fr" ? "Photos & documents" : "Photos & documents"}</p>
        <span className="mono text-xs text-zinc-500">{files.length} {lang === "fr" ? "fichiers" : "files"}</span>
      </div>

      <div
        data-testid="file-dropzone"
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        className="m-5 border border-dashed border-zinc-300 p-8 text-center cursor-pointer hover:border-orange-600 hover:bg-orange-50/30 transition-colors"
      >
        <UploadSimple size={28} className="mx-auto text-zinc-400" weight="duotone" />
        <p className="mt-3 text-sm text-zinc-700 font-medium">
          {uploading ? (lang === "fr" ? "Téléversement…" : "Uploading…") : (lang === "fr" ? "Glissez-déposez ou cliquez (JPG, PNG, PDF · max 15MB)" : "Drag & drop or click (JPG, PNG, PDF · max 15MB)")}
        </p>
        <input
          data-testid="file-input"
          ref={inputRef}
          type="file"
          multiple
          accept="image/jpeg,image/png,image/webp,image/gif,application/pdf"
          className="hidden"
          onChange={(e) => handleFiles(Array.from(e.target.files || []))}
        />
      </div>

      {files.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-px bg-zinc-200 border-t border-zinc-200">
          {files.map((f) => (
            <div key={f.id} data-testid={`file-${f.id}`} className="bg-white p-3 relative group">
              {f.content_type?.startsWith("image/") ? (
                thumbs[f.id] ? (
                  <img src={thumbs[f.id]} alt={f.original_filename} className="w-full h-32 object-cover border border-zinc-200" />
                ) : (
                  <div className="w-full h-32 bg-zinc-100 flex items-center justify-center border border-zinc-200">
                    <ImageIcon size={32} className="text-zinc-400" />
                  </div>
                )
              ) : (
                <div className="w-full h-32 bg-zinc-50 flex items-center justify-center border border-zinc-200">
                  <FilePdf size={40} className="text-orange-600" weight="duotone" />
                </div>
              )}
              <div className="mt-2 text-xs truncate text-zinc-700">{f.original_filename}</div>
              <button
                onClick={() => remove(f.id)}
                className="absolute top-2 right-2 bg-white border border-zinc-300 p-1 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-orange-600 hover:text-white"
                data-testid={`file-delete-${f.id}`}
              >
                <Trash size={12} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
